import "./MoveModal.css";
import React, { useCallback, useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";

const NEW_SITE_URL = "https://rechatai.org";
const SUPPORT_EMAIL = "hello@rechatai.org";
// Pre-filled subject so replies land already triaged as move-related.
const SUPPORT_MAILTO =
  "mailto:" + SUPPORT_EMAIL + "?subject=Help%20moving%20to%20rechatai.org";
const DISMISS_KEY = "rechat_move_notice_dismissed";

// sessionStorage, not localStorage: the notice should not nag while someone
// browses, but a fresh visit is exactly when it needs to be seen again. Wrapped
// because Safari private mode throws on access rather than returning null.
const wasDismissed = () => {
  try {
    return window.sessionStorage.getItem(DISMISS_KEY) === "1";
  } catch (e) {
    return false;
  }
};

const rememberDismissal = () => {
  try {
    window.sessionStorage.setItem(DISMISS_KEY, "1");
  } catch (e) {
    /* storage unavailable — the modal simply reappears on the next route */
  }
};

const FOCUSABLE = 'a[href], button:not([disabled])';

// Relocation notice shown once per browsing session on the legacy domain.
// The HelloBar is the permanent reminder; this is the one-time interruption,
// so every exit is offered: close button, Escape, backdrop, and "Not now".
function MoveModal() {
  const [open, setOpen] = useState(() => !wasDismissed());
  const cardRef = useRef(null);
  const previouslyFocused = useRef(null);

  const close = useCallback(() => {
    rememberDismissal();
    setOpen(false);
  }, []);

  useEffect(() => {
    if (!open) return undefined;

    previouslyFocused.current = document.activeElement;
    const restoreOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    // Focus the dialog itself rather than the close button: screen readers
    // still announce it and Tab is still trapped, but a mouse user does not
    // land on a highlighted control the moment the page opens.
    if (cardRef.current) cardRef.current.focus();

    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        close();
        return;
      }
      if (event.key !== "Tab" || !cardRef.current) return;

      // Keep Tab inside the dialog — without this the user tabs into the page
      // behind the scrim, which they cannot see.
      const items = cardRef.current.querySelectorAll(FOCUSABLE);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = restoreOverflow;
      const previous = previouslyFocused.current;
      if (previous && typeof previous.focus === "function") previous.focus();
    };
  }, [open, close]);

  if (!open) return null;

  return ReactDOM.createPortal(
    <div
      className="MoveModal__scrim"
      // mousedown, not click: a text selection that starts inside the card and
      // releases on the backdrop should not count as dismissing it.
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) close();
      }}
    >
      <div
        className="MoveModal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="MoveModal__title"
        aria-describedby="MoveModal__desc"
        ref={cardRef}
        tabIndex={-1}
      >
        <button
          type="button"
          className="MoveModal__close"
          onClick={close}
          aria-label="Close notice"
        >
          <svg
            viewBox="0 0 24 24"
            width="20"
            height="20"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            aria-hidden="true"
            focusable="false"
          >
            <line x1="6" y1="6" x2="18" y2="18" />
            <line x1="18" y1="6" x2="6" y2="18" />
          </svg>
        </button>

        <h2 id="MoveModal__title" className="MoveModal__title">
          We&rsquo;ve Moved
        </h2>

        <div id="MoveModal__desc">
          <p className="MoveModal__body">
            Reso.chat is now{" "}
            <span className="MoveModal__domain">rechatai.org</span>.
            We&rsquo;ve moved to a new home with a new name, but you can still
            expect the same service and experience you know.
          </p>
          <p className="MoveModal__body">
            Visit us at{" "}
            <span className="MoveModal__domain">rechatai.org</span> and continue
            where you left off.
          </p>
          <p className="MoveModal__help">
            Run into any trouble moving? Email us at{" "}
            <a className="MoveModal__mail" href={SUPPORT_MAILTO}>
              {SUPPORT_EMAIL}
            </a>{" "}
            and we&rsquo;ll help.
          </p>
        </div>

        <div className="MoveModal__actions">
          <a className="MoveModal__cta" href={NEW_SITE_URL}>
            Visit rechatai.org
            <svg
              className="MoveModal__arrow"
              viewBox="0 0 24 24"
              width="18"
              height="18"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
              focusable="false"
            >
              <line x1="5" y1="12" x2="19" y2="12" />
              <polyline points="12 5 19 12 12 19" />
            </svg>
          </a>
          <button type="button" className="MoveModal__later" onClick={close}>
            Not now
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default MoveModal;
